import React, { useCallback, useEffect, useState } from 'react';

import { IS_PRODUCTION } from 'utils/globals';

import Button from 'components/shared/Button';
import { useExperiment, emitter, experimentDebugger } from '@marvelapp/react-ab-test';
import ShufflerProductTile from './ShufflerProductTile';
import ShufflerProduct, { propInShufflerProduct } from './ShufflerProduct';

import { type ShufflerFilterGroup, type ShufflerFilterItem } from './data/filters.types';

emitter.defineVariants('Placeholder vs Sample', ['Show Placeholder', 'Show Sample']);
experimentDebugger.setDebuggerAvailable(!IS_PRODUCTION);
experimentDebugger.enable();

type ShufflerWalkthroughProps = {
  items: ShufflerProduct[];
  filters?: ShufflerFilterGroup[];
  featured: ShufflerProduct[];
  handleShowAdvancedFilters: (show: boolean) => void;
  setProduct: (product?: ShufflerProduct) => void;
  selectedFilters: Set<string>;
  setSelectedFilters: (selected: Set<string>) => void;
  setShowWalkthrough: (set: boolean) => void;
};

type ExampleCard = {
  src?: string;
  format: string;
  orientation: string;
};

function ShufflerWalkthrough(props: ShufflerWalkthroughProps) {
  const {
    items: visibleItems,
    filters,
    featured: featuredProducts,
    handleShowAdvancedFilters,
    setProduct,
    selectedFilters,
    setSelectedFilters,
    setShowWalkthrough,
  } = props;

  const hasFlat = !!visibleItems.find((c) => c.format === 'Flat');
  const hasSingleFold = !!visibleItems.find((c) => c.format === 'Single Fold');
  const hasMultiFold = !!visibleItems.find((c) => c.format === 'Multi Fold');
  const hasVertical = !!visibleItems.find((c) => c.orientation === 'Vertical');
  const hasHorizontal = !!visibleItems.find((c) => c.orientation === 'Horizontal');
  const hasSquare = !!visibleItems.find((c) => c.orientation === 'Square');

  const [occasion, setOccasion] = useState<string | undefined>(undefined);
  const [format, setFormat] = useState<string | undefined>(undefined);
  const [orientation, setOrientation] = useState<string | undefined>(undefined);

  const foundFilter = filters?.find((f) => {
    return f.groupname === 'collections';
  }) as ShufflerFilterGroup | undefined;

  if (!foundFilter) return null;

  const { groupfilters: occasions } = foundFilter;

  const selectOccasion = useCallback(
    ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
      const { options, selectedIndex } = target;
      const { value } = options[selectedIndex];
      const resetFilters = new Set(
        [...selectedFilters].filter((f) => f.split(':').pop()?.toLowerCase() !== 'collections')
      );
      setSelectedFilters(resetFilters.add(`${value}:collections`));
      setOccasion(value);
    },
    [selectedFilters, setSelectedFilters]
  );

  const unsetOccasion = useCallback(() => {
    setSelectedFilters(
      new Set([...selectedFilters].filter((f) => f.split(':').pop()?.toLowerCase() !== 'collections'))
    );
    setOccasion(undefined);
  }, [selectedFilters, setSelectedFilters]);

  const selectFormat = useCallback(
    (value: string) => {
      if (value !== 'None') {
        setSelectedFilters(new Set([...selectedFilters.add(`${value}:format`)]));
      }
      setFormat(value);
    },
    [selectedFilters, setSelectedFilters]
  );

  const unsetFormat = useCallback(() => {
    setSelectedFilters(
      new Set([...selectedFilters].filter((f) => f.split(':').pop()?.toLowerCase() !== 'format'))
    );
    setFormat(undefined);
  }, [selectedFilters, setSelectedFilters]);

  const selectOrientation = useCallback(
    (value: string) => {
      if (value !== 'None') {
        setSelectedFilters(new Set([...selectedFilters.add(`${value}:orientation`)]));
      }
      setOrientation(value);
    },
    [selectedFilters, setSelectedFilters]
  );

  const checkOccasionSelected = useCallback(() => {
    if (occasion && !selectedFilters.has(`${occasion}:collections`)) {
      unsetOccasion();
    }
  }, [occasion, selectedFilters, unsetOccasion]);

  const checkFormatSelected = useCallback(() => {
    if (format && !selectedFilters.has(`${format}:format`)) {
      unsetFormat();
    }
  }, [format, selectedFilters, unsetFormat]);

  useEffect(() => {
    checkOccasionSelected();
    checkFormatSelected();
  }, [checkFormatSelected, checkOccasionSelected, selectedFilters]);

  useEffect(() => {
    if (occasion && format && orientation) {
      setShowWalkthrough(false);
    }
  }, [occasion, format, orientation, setShowWalkthrough]);

  const getRandomItem = useCallback(
    (f: Partial<ExampleCard>) => {
      function passFilters(item: ShufflerProduct) {
        return [...Object.entries(f)].every(
          ([key, val]) => propInShufflerProduct(key) && item[key] === val
        );
      }
      const a = visibleItems.filter(passFilters);
      const r = Math.floor(Math.random() * a.length);

      return a[r];
    },
    [visibleItems]
  );

  function getPlaceHolder(f: Partial<ExampleCard>) {
    const pdata: ExampleCard[] = [
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P57Ph.png',
        format: 'Flat',
        orientation: 'Horizontal',
      },
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P57Pv.png',
        format: 'Flat',
        orientation: 'Vertical',
      },
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P57Fh.png',
        format: 'Single Fold',
        orientation: 'Horizontal',
      },
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P57Fv.png',
        format: 'Single Fold',
        orientation: 'Vertical',
      },
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P455Av.png',
        format: 'Multi Fold',
        orientation: 'Vertical',
      },
      {
        src: 'http://dl.bayphoto.com/wos_assets/icon_P55T_SideFold.png',
        format: 'Multi Fold',
        orientation: 'Square',
      },
    ];

    const found =
      pdata.find(
        (p) =>
          (!f.format || f.format === p.format) &&
          (!f.orientation || f.orientation === p.orientation)
      ) ||
      pdata.find((p) => f.orientation === p.orientation) ||
      pdata.find((p) => f.format === p.format);

    return found || { src: '' };
  }
  const { selectVariant } = useExperiment('Placeholder vs Sample');
  const variant = selectVariant({ 'Show Placeholder': true, 'Show Sample': false });

  const getExampleCard = useCallback(
    (f: Partial<ExampleCard>) => {
      const alwaysShowPlaceholder = variant;
      const r = !alwaysShowPlaceholder && getRandomItem(f);
      return r ? r.imageUrl : getPlaceHolder(f).src;
    },
    [getRandomItem, variant]
  );

  return (
    <div className="cards-walkthrough-wrapper">
      <div className="cards-walkthrough-container">
        <Button
          a11yText="Close Card Wizard"
          className="close"
          type="icon"
          icon="times"
          onClick={() => setShowWalkthrough(false)}
        />
        {!occasion && (
          <React.Fragment>
            <div className="walkthrough-header">
              <h1>Create the Perfect Card!</h1>
            </div>
            <div className="cards-walkthrough-occasion">
              <span>Start with a blank size below, or </span>
              <div className="cards-walkthrough-select-container">
                <select onChange={selectOccasion}>
                  <option>Choose an occasion</option>
                  {occasions.map(({ name, description, products }) => (
                    <option
                      key={name}
                      value={name}
                      data-desc={description}
                      data-count={products.length}
                    >
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="cards-walkthrough-suggested">
              {featuredProducts.map((featuredProduct) => (
                <ShufflerProductTile
                  key={`${featuredProduct.uid}`}
                  shufflerProduct={featuredProduct}
                  numIcons={5}
                  height={22}
                  click={() => {
                    setShowWalkthrough(false);
                    handleShowAdvancedFilters(false);
                    setProduct(undefined);
                  }}
                />
              ))}
            </div>
          </React.Fragment>
        )}
        {occasion && !format && (
          <React.Fragment>
            <div className="walkthrough-header">
              <strong>{`Let's find a card for ${occasion}`}</strong>
              <h1>Select the format that works best for you.</h1>
            </div>
            <div className="cards-walkthrough-format">
              <div className="cards-walkthrough-panel-container">
                <div className={`cards-walkthrough-panel ${hasFlat ? 'active' : 'inactive'}`}>
                  <div className="panel-header">
                    <strong>Flat</strong>
                    <span>Easy to mail. Simple and classic</span>
                  </div>
                  <Button type="link" onClick={() => selectFormat('Flat')} disabled={!hasFlat}>
                    <img
                      src={getExampleCard({
                        format: 'Flat',
                        orientation: orientation || 'Vertical',
                      })}
                      alt="Flat cards"
                    />
                  </Button>
                  <Button onClick={() => selectFormat('Flat')} disabled={!hasFlat}>
                    Flat Card
                  </Button>
                </div>
                <div className={`cards-walkthrough-panel ${hasSingleFold ? 'active' : 'inactive'}`}>
                  <div className="panel-header">
                    <strong>Single Fold</strong>
                    <span>Very popular. Ideal for small number of photos</span>
                  </div>
                  <Button
                    type="link"
                    onClick={() => selectFormat('Single Fold')}
                    disabled={!hasSingleFold}
                  >
                    <img
                      src={getExampleCard({
                        format: 'Single Fold',
                        orientation: orientation || 'Vertical',
                      })}
                      alt="Single fold cards"
                    />
                  </Button>
                  <Button onClick={() => selectFormat('Single Fold')} disabled={!hasSingleFold}>
                    Single Fold Card
                  </Button>
                </div>
                <div className={`cards-walkthrough-panel ${hasMultiFold ? 'active' : 'inactive'}`}>
                  <div className="panel-header">
                    <strong>Multi Fold</strong>
                    <span>Say something extra. Great for more photos</span>
                  </div>
                  <Button
                    type="link"
                    onClick={() => selectFormat('Multi Fold')}
                    disabled={!hasMultiFold}
                  >
                    <img
                      src={getExampleCard({
                        format: 'Multi Fold',
                        orientation: orientation || 'Vertical',
                      })}
                      alt="Multi fold cards"
                    />
                  </Button>
                  <Button onClick={() => selectFormat('Multi Fold')} disabled={!hasMultiFold}>
                    Multi Fold Card
                  </Button>
                </div>
              </div>
            </div>
            <div className="button-footer">
              <Button type="bare" onClick={() => unsetOccasion()}>
                Go Back and Select Another Occasion
              </Button>
            </div>
          </React.Fragment>
        )}
        {occasion && format && !orientation && (
          <React.Fragment>
            <strong>
              Finding a {format} card for {occasion}
            </strong>
            <h1>Choose card orientation.</h1>
            <div className="cards-walkthrough-orientation">
              <div className="cards-walkthrough-panel-container">
                <div className={`cards-walkthrough-panel ${hasVertical ? 'active' : 'inactive'}`}>
                  <strong>Vertical</strong>
                  <span>Portrait</span>
                  <Button
                    type="link"
                    onClick={() => selectOrientation('Vertical')}
                    disabled={!hasVertical}
                  >
                    <img
                      src={getExampleCard({ format, orientation: 'Vertical' })}
                      alt="Vertical cards"
                    />
                  </Button>
                  <Button onClick={() => selectOrientation('Vertical')} disabled={!hasVertical}>
                    Vertical Card
                  </Button>
                </div>
                <div className={`cards-walkthrough-panel ${hasHorizontal ? 'active' : 'inactive'}`}>
                  <strong>Horizontal</strong>
                  <span>Landscape</span>
                  <Button
                    type="link"
                    onClick={() => selectOrientation('Horizontal')}
                    disabled={!hasHorizontal}
                  >
                    <img
                      src={getExampleCard({ format, orientation: 'Horizontal' })}
                      alt="Horizontal cards"
                    />
                  </Button>
                  <Button onClick={() => selectOrientation('Horizontal')} disabled={!hasHorizontal}>
                    Horizontal Card
                  </Button>
                </div>
                <div className={`cards-walkthrough-panel ${hasSquare ? 'active' : 'inactive'}`}>
                  <strong>Square</strong>
                  <span>Fun and stylish</span>
                  <Button
                    type="link"
                    onClick={() => selectOrientation('Square')}
                    disabled={!hasSquare}
                  >
                    <img src={getExampleCard({ orientation: 'Square' })} alt="Square cards" />
                  </Button>
                  <Button onClick={() => selectOrientation('Square')} disabled={!hasSquare}>
                    Square
                  </Button>
                </div>
              </div>
            </div>
            {visibleItems.length > 0 && (
              <div className="button-container">
                <small>No preference?</small>
                <Button type="confirm" onClick={() => selectOrientation('None')}>
                  Show me all of them!
                </Button>
              </div>
            )}
            <div className="button-footer">
              <Button
                type="bare"
                onClick={() => {
                  setSelectedFilters(
                    new Set([...selectedFilters].filter((f) => !f.match(':format')))
                  );
                  setFormat(undefined);
                }}
              >
                Go Back and Select Another Format
              </Button>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default ShufflerWalkthrough;
